import { useMemo } from "react"

import { useSkuStore } from "~/providers/sku-store-provider"
import { api } from "~/utils/api"
import SuperChart, { type CategoryChartSeries } from "./SuperChart"
import LoadingOverlay from "../LoadingOverlay"
import { type CustomTooltipProps } from "ag-grid-react"
import HeaderTooltip from "../table/tooltips/HeaderTooltip"
import { Card, CardContent, CardHeader, CardTitle } from "../Card"
import { ChartType } from "~/constants/graphs"

type Props = {
  customerIds: string[]
  variantIds: string[]
}

export const OrderFrequencyVarianceTooltip = (props: CustomTooltipProps) => {
  if (props.location === "header") {
    return <HeaderTooltip {...props} />
  }
  const variantIds = props.node?.group ? props.value.variantIds : [props.data.variantId]
  const customerIds = props.node?.group ? props.value.customerIds : [props.data.customerId]
  return (
    <div className="w-[500px] mr-6 z-10">
      <OrderFrequencyVarianceGraph customerIds={customerIds} variantIds={variantIds} />
    </div>
  )
}

export function OrderFrequencyVarianceGraph({ customerIds, variantIds }: Props) {
  const warehouseId = useSkuStore((state) => state.warehouseId)
  const { data: distribution, isLoading } =
    api.customer.getOrderFrequencyVarianceDistribution.useQuery(
      { customerIds, variantIds, warehouseId },
      { enabled: !!customerIds.length && !!variantIds.length },
    )

  const series: CategoryChartSeries = useMemo(
    () => ({
      name: "Orders",
      data:
        distribution?.map((d) => ({ category: String(d.deviationValue), value: d.count })) ?? [],
      type: "column",
      colors: ["rgb(251, 146, 60)"],
    }),
    [distribution],
  )

  const content = isLoading ? (
    <LoadingOverlay />
  ) : (
    <div className="flex flex-col gap-3">
      <p className="paragraph-sm text-fg-muted">
        Distribution of order intervals vs. average frequency. Each bar shows how many intervals
        deviated by that many days from the average. Based on the most recent 20% of orders (min
        10).
      </p>
      <SuperChart
        type={ChartType.CategorySeries}
        data={[series]}
        noLegend
        height={180}
        showTooltip
        customTooltipFormatter={function () {
          return `<span style="display:block;padding:6px 10px"><b>${this.key} days</b> deviation · <b>${this.y}</b> interval${(this.y ?? 0) !== 1 ? "s" : ""}</span>`
        }}
      />
    </div>
  )

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>Order frequency variance</CardTitle>
      </CardHeader>
      <CardContent>{content}</CardContent>
    </Card>
  )
}
